import { useEffect, useState } from 'react';
import {
  CURRENT_NOTEBOOK_ALIASES,
  notebooklmApi,
  type NotebookAuthStatus,
  type NotebookQueryResponse,
  type NotebookSummary,
} from '@/api/notebooklm';

export function NotebookLMTab() {
  const [authStatus, setAuthStatus] = useState<NotebookAuthStatus | null>(null);
  const [notebooks, setNotebooks] = useState<NotebookSummary[]>([]);
  const [selectedNotebookId, setSelectedNotebookId] = useState('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState<NotebookQueryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [querying, setQuerying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.allSettled([notebooklmApi.authStatus(), notebooklmApi.listNotebooks()])
      .then(([authResult, notebooksResult]) => {
        if (cancelled) return;

        if (authResult.status === 'fulfilled') {
          setAuthStatus(authResult.value);
        } else {
          setAuthStatus(null);
        }

        if (notebooksResult.status === 'fulfilled') {
          const nextNotebooks = notebooksResult.value.notebooks ?? [];
          setNotebooks(nextNotebooks);
          setSelectedNotebookId(nextNotebooks[0]?.id ?? '');
        } else {
          setError(
            notebooksResult.reason instanceof Error
              ? notebooksResult.reason.message
              : 'Failed to load NotebookLM notebooks',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleQuery = async () => {
    if (!selectedNotebookId || !question.trim()) return;
    setQuerying(true);
    setError(null);
    setAnswer(null);
    try {
      setAnswer(await notebooklmApi.query(selectedNotebookId, question.trim()));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'NotebookLM query failed');
    } finally {
      setQuerying(false);
    }
  };

  return (
    <div className="p-6 font-mono text-sm text-gray-400 space-y-5">
      <div className="flex items-baseline justify-between">
        <div className="text-xs uppercase tracking-wider text-gray-600">NotebookLM Bridge</div>
        <span className="text-[10px] uppercase tracking-wider text-yellow-500/80">
          Sync disabled until CES bridge is verified
        </span>
      </div>

      <div className="rounded border border-gray-800 bg-black/20 p-3 text-xs">
        {loading ? (
          <span className="text-gray-500">Checking Flash/NotebookLM bridge...</span>
        ) : authStatus ? (
          <span className={authStatus.authenticated ? 'text-[#39FF14]' : 'text-yellow-400'}>
            {authStatus.message}
          </span>
        ) : (
          <span className="text-yellow-400">Auth status endpoint unavailable.</span>
        )}
      </div>

      {error && (
        <div className="rounded border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-300">
          {error}
        </div>
      )}

      <div className="grid grid-cols-[320px_1fr] gap-4">
        <section className="space-y-3">
          <label className="block text-[10px] uppercase tracking-wider text-gray-600">
            Notebook
            <select
              className="mt-1 w-full rounded border border-gray-800 bg-black px-2 py-2 text-xs text-gray-200"
              value={selectedNotebookId}
              onChange={(event) => setSelectedNotebookId(event.target.value)}
              disabled={notebooks.length === 0}
            >
              {notebooks.length === 0 ? (
                <option value="">No notebooks returned</option>
              ) : (
                notebooks.map((notebook) => (
                  <option key={notebook.id} value={notebook.id}>
                    {notebook.title} ({notebook.source_count ?? 0})
                  </option>
                ))
              )}
            </select>
          </label>

          <div className="rounded border border-gray-800 p-3">
            <div className="text-[10px] uppercase tracking-wider text-gray-600 mb-2">
              Current aliases to verify
            </div>
            <div className="flex flex-wrap gap-1">
              {CURRENT_NOTEBOOK_ALIASES.map((alias) => (
                <span key={alias} className="rounded bg-gray-900 px-2 py-1 text-[10px] text-gray-500">
                  {alias}
                </span>
              ))}
            </div>
          </div>

          <button
            type="button"
            disabled
            className="w-full rounded border border-gray-800 px-3 py-2 text-left text-xs text-gray-600 cursor-not-allowed"
          >
            Sync is disabled until the CES `nlm-auto-sync.py` bridge is verified.
          </button>
        </section>

        <section className="space-y-3">
          <textarea
            className="h-28 w-full rounded border border-gray-800 bg-black p-3 text-sm text-gray-200"
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            placeholder="Ask a source-grounded question..."
          />
          <button
            type="button"
            onClick={() => void handleQuery()}
            disabled={!selectedNotebookId || !question.trim() || querying}
            className="rounded border border-[#39FF14]/40 px-4 py-2 text-xs uppercase tracking-wider text-[#39FF14] disabled:border-gray-800 disabled:text-gray-600"
          >
            {querying ? 'Querying...' : 'Query Notebook'}
          </button>

          {answer && (
            <div className="rounded border border-gray-800 bg-black/20 p-4">
              <div className="text-[10px] uppercase tracking-wider text-gray-600 mb-2">
                Answer
              </div>
              <p className="whitespace-pre-wrap text-gray-200">{answer.answer}</p>
              <div className="mt-3 text-xs text-gray-500">
                Sources: {Array.isArray(answer.sources_used) ? answer.sources_used.length : 0} · Citations:{' '}
                {answer.citations ? Object.keys(answer.citations).length : 0}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
